import { ref, onMounted, computed } from '@vue/composition-api';
import { CouponDetail, CouponDetailTransState, qryCouponDetail, CouponDetailType } from '@tencent/wphk-act-base-cgi';
import { getServerTime } from '@tencent/wphk-components-common/utils/server-time';
import { i18n } from '@/i18n';
import { amount, to } from '@tencent/wphk-base-utils';
import dayjs from 'dayjs';
import { language, CustomException } from '@tencent/ppd-base-libs';
import { formatDiscount } from '@/api/coupon-list';
import { biBrow } from '@tencent/fit-bi-sdk';

const lang = ref(language.getLang() as string);

type BtnInfo = {
  btnText: string,
  btnStatus: string,
};
/**
 * 获取按钮信息
 */
function getBtnInfo(couponDetail: CouponDetail): BtnInfo {
  const { validTime, expireTime, transState, useChannel, shareType } = couponDetail;
  const now = getServerTime();
  let btnStatus = 'inactive';
  if (now < dayjs(validTime).toDate()) {
    return {
      btnText: i18n('未到可用时间'),
      btnStatus,
    };
  }

  if (now > expireTime) {
    return {
      btnText: i18n('已失效'),
      btnStatus,
    };
  }

  let btnText = '';

  switch (transState) {
    case CouponDetailTransState.UNUSED:
      if (useChannel === 1) {
        btnText = i18n('用券付款');
      } else {
        btnText = i18n('立即使用');
      }
      btnStatus = '';
      break;
    case CouponDetailTransState.USED:
      if (shareType === 1) {
        btnText = i18n('已使用');
      }
      break;
    case CouponDetailTransState.EXPIRED:
      btnText = i18n('已失效');
      break;
    case CouponDetailTransState.REFUNDED:
      btnText = i18n('已退款');
      break;
    case CouponDetailTransState.PREUSED:
      btnText = i18n('已使用');
      break;
  }

  return {
    btnText,
    btnStatus,
  };
}

/**
 * 获取券使用要求
 */
function getRequiredContent(couponDetail: CouponDetail) {
  const { requiredNum, maxDiscount, couponType } = couponDetail;
  const yuanRequiredNum = amount.fen2Yuan(requiredNum);
  if (couponType === CouponDetailType.DISCOUNT_COUPON) {
    if (+requiredNum <= 1) {
      return i18n('最高可优惠多少元', {
        maxDiscount,
      });
    }
    return `${i18n('付款滿HK$XX可使用，最高可優惠HK$XX', { requiredNum: yuanRequiredNum, maxDiscount })}`;
  }
  return i18n('满HK$多少可使用', {
    requiredNum: yuanRequiredNum,
  });
}

/**
 * 获取有效期限的内容
 */
function getValidityContent(couponDetail: CouponDetail) {
  const { validTime, expireTime } = couponDetail;
  const fValidTime = dayjs(validTime).format('YYYY-MM-DD');
  const fExpireTime = dayjs(expireTime).format('YYYY-MM-DD');
  return `${fValidTime}${i18n('至')}${fExpireTime}`;
}

/**
 * 获取金额内容
 */
function getAmountContent(couponDetail: CouponDetail) {
  const { discount } = couponDetail;
  return discount;
}

export function useCouponDetail(userCouponId: string) {
  // 详情按钮信息
  const btnInfo = ref<null | BtnInfo>({
    btnText: '',
    btnStatus: '',
  });
  const couponDetail = ref<null | CouponDetail>(null);
  // 券使用要求
  const requiredContent = ref('');
  // 券的有效期限
  const validityContent = ref('');
  // 展示金额内容
  const amountContent = ref('');
  // 数据是否准备好
  const isCouponDetailReady = ref(false);

  const biExt = computed(() => ({
    ftransstate: couponDetail.value?.transState || '',
    fcouponname: couponDetail.value?.couponName || '',
    fcouponid: couponDetail.value?.couponId || '',
  }));


  onMounted(async () => {
    const [err, couponDetailRes] = await to(qryCouponDetail(userCouponId));
    if (err) {
      throw new CustomException(err, 'getCouponDetailFail', '获取优惠券详情失败');
    }
    const { couponDetail: couponData } = couponDetailRes;
    requiredContent.value = getRequiredContent(couponData);
    validityContent.value = getValidityContent(couponData);
    amountContent.value = getAmountContent(couponData);
    btnInfo.value = getBtnInfo(couponData);
    couponDetail.value = couponData;
    isCouponDetailReady.value = true;
    biBrow('coupon-detail.page_brow|页面曝光', biExt.value);
  });

  return {
    btnInfo,
    requiredContent,
    validityContent,
    amountContent,
    couponDetail,
    lang,
    isCouponDetailReady,
    biExt,
  };
}
